import {Item, ItemType, Workspace} from '../../../../API';
import {TENANT_ID, WORKSPACE_ID, CLIENT_USER_ID} from '../fixtures';

// ── Stable IDs ──────────────────────────────────────────────────────────────
const LIBRARY_ID       = 'demo-lib1-0000-0000-0000-000000000000';

const SEC_ONBOARDING   = 'demo-sec1-0001-0000-0000-000000000001';
const SEC_POLICIES     = 'demo-sec2-0002-0000-0000-000000000002';
const LEAF_WELCOME     = 'demo-leaf-0001-0000-0000-000000000001';
const LEAF_FIRSTWEEK   = 'demo-leaf-0002-0000-0000-000000000002';
const LEAF_TOOLS       = 'demo-leaf-0003-0000-0000-000000000003';
const LEAF_CONDUCT     = 'demo-leaf-0004-0000-0000-000000000004';
const LEAF_SECURITY    = 'demo-leaf-0005-0000-0000-000000000005';

const NOW = new Date('2026-01-01T00:00:00.000Z').toISOString();

function leaf(id: string, parentId: string, name: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LEAF,
        childIds: '[]',
        parentIds: JSON.stringify([parentId]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function library(id: string, name: string, childIds: string[], slug: string): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.LIBRARY,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([WORKSPACE_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

function cluster(id: string, name: string, childIds: string[]): Item {
    return {
        __typename: 'Item',
        id,
        ownerId: TENANT_ID,
        workspaceId: WORKSPACE_ID,
        name,
        type: ItemType.CLUSTER,
        childIds: JSON.stringify(childIds),
        parentIds: JSON.stringify([LIBRARY_ID]),
        readRoles: '[]',
        writeRoles: '[]',
        creator: CLIENT_USER_ID,
        updatedBy: CLIENT_USER_ID,
        archived: false,
        trashed: false,
        pinned: false,
        pinnedIds: '[]',
        createdAt: NOW,
        updatedAt: NOW,
        version: 1,
        deletedAt: null,
        slug: null,
        description: null,
        acknowledgement: null,
        acknowledgementRequired: null,
        ackUpdatedBy: null,
        statusRestoreParentIds: null,
        statusSourceLibraryId: null,
        statusUpdatedBy: null,
    };
}

// ── Items ────────────────────────────────────────────────────────────────────
export const items: Item[] = [
    library(LIBRARY_ID, 'Main Library', [SEC_ONBOARDING, SEC_POLICIES], 'main'),

    cluster(SEC_ONBOARDING, 'Onboarding', [LEAF_WELCOME, LEAF_FIRSTWEEK, LEAF_TOOLS]),
    cluster(SEC_POLICIES, 'Company Policies', [LEAF_CONDUCT, LEAF_SECURITY]),
    leaf(LEAF_WELCOME, SEC_ONBOARDING, 'Welcome to Acme'),
    leaf(LEAF_FIRSTWEEK, SEC_ONBOARDING, 'Your First Week'),
    leaf(LEAF_TOOLS, SEC_ONBOARDING, 'Tools & Access'),
    leaf(LEAF_CONDUCT, SEC_POLICIES, 'Code of Conduct'),
    leaf(LEAF_SECURITY, SEC_POLICIES, 'Security Policy'),
];

// ── Workspace override ────────────────────────────────────────────────────────
export const workspaceOverride: Partial<Workspace> = {
    childIds: JSON.stringify([LIBRARY_ID]),
    name: 'Acme Corp',
};

// ── ProseMirror document content keyed by itemId ─────────────────────────────
export const docContents: Record<string, any> = {
    [LEAF_WELCOME]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Welcome to Acme Corp'}]},
            {type: 'paragraph', content: [{type: 'text', text: "We're thrilled to have you on the team. This guide covers everything you need to hit the ground running in your first week."}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Your first day checklist'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Set up your email and Slack account'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Complete your security training in this knowledge base'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Meet your team lead for a 30-minute intro call'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Request access to the tools listed in Tools & Access'}]}]},
            ]},
            {type: 'paragraph', content: [{type: 'text', text: "If anything is unclear, ping your buddy in Slack — they're expecting to hear from you."}]},
        ],
    },
    [LEAF_FIRSTWEEK]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Your First Week'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Here is what a typical first week looks like at Acme Corp.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Day 1–2 — Setup & orientation'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Focus on getting your environment working. Do not worry about shipping anything yet.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Day 3–4 — Shadow your team'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Join your team in standups, read through recent PRs, and ask a lot of questions.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Day 5 — Your first task'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Pick up a starter ticket from the backlog. Your lead will flag a few good candidates.'}]},
        ],
    },
    [LEAF_TOOLS]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Tools & Access'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Request access to the following tools through IT. Most approvals happen within 24 hours.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Required tools'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'GitHub — source control and code review'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Linear — issue tracking'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Figma — design files (read access by default)'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'AWS Console — production access requires a separate approval from your manager'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'VPN setup'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Download the VPN client from the IT portal. You need it to access staging and internal dashboards.'}]},
        ],
    },
    [LEAF_CONDUCT]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Code of Conduct'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Everyone at Acme Corp is expected to contribute to a respectful and inclusive environment.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Our commitments'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Treat colleagues with respect regardless of background or seniority'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Assume good intent; ask before concluding wrongdoing'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Raise concerns directly with your manager or HR'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Unacceptable behaviour'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Harassment, discrimination, and retaliation will not be tolerated. Any report is investigated promptly and confidentially.'}]},
        ],
    },
    [LEAF_SECURITY]: {
        type: 'doc',
        content: [
            {type: 'heading', attrs: {level: 1}, content: [{type: 'text', text: 'Security Policy'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Security is everyone\'s responsibility. Following these rules protects the company and our customers.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Passwords & authentication'}]},
            {type: 'list', attrs: {type: 'bullet'}, content: [
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Use a password manager. Never reuse passwords.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Enable two-factor authentication on all company accounts.'}]}]},
                {type: 'listItem', attrs: {checked: null}, content: [{type: 'paragraph', content: [{type: 'text', text: 'Never share credentials over Slack, email, or anywhere else.'}]}]},
            ]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Devices'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Company devices must be encrypted and locked when unattended. Report a lost or stolen device to IT within the hour.'}]},
            {type: 'heading', attrs: {level: 2}, content: [{type: 'text', text: 'Incidents'}]},
            {type: 'paragraph', content: [{type: 'text', text: 'Spotted a phishing email or suspicious behaviour? Report it to security@acme.example immediately. No blame for reporting in good faith.'}]},
        ],
    },
};
